import { Core } from '@pdftron/webviewer'

export const createBringToFrontAction = (
  annotationManager: Core.AnnotationManager,
): (() => void) => {
  return () => {
    const annotations = annotationManager.getSelectedAnnotations()
    annotations.forEach(annotation => {
      annotationManager.bringToFront(annotation)
      annotationManager.redrawAnnotation(annotation)
    })
  }
}

export const createBringToBackAction = (
  annotationManager: Core.AnnotationManager,
): (() => void) => {
  return () => {
    const annotations = annotationManager.getSelectedAnnotations()
    annotations.forEach(annotation => {
      annotationManager.bringToBack(annotation)
      annotationManager.redrawAnnotation(annotation)
    })
  }
}

export const createCopyAction = (annotationManager: Core.AnnotationManager): (() => void) => {
  return () => {
    const annotations = annotationManager.getSelectedAnnotations()
    if (annotations.length > 0) {
      annotationManager.exportAnnotations({ annotList: annotations }).then(xfdfString => {
        localStorage.setItem('copiedAnnotation', xfdfString)
      })
    }
  }
}

export const createPasteAction = (annotationManager: Core.AnnotationManager): (() => void) => {
  return () => {
    const xfdfString = localStorage.getItem('copiedAnnotation')
    if (xfdfString) {
      annotationManager.importAnnotations(xfdfString)
    }
  }
}

export const createCopyEvents = (
  annotationManager: Core.AnnotationManager,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ((evt: any) => void) => {
  const copyAction = createCopyAction(annotationManager)

  const pasteAction = createPasteAction(annotationManager)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (evt: any) => {
    if (evt.key === 'c' && evt.ctrlKey) {
      copyAction()
    }
    if (evt.key === 'v' && evt.ctrlKey) {
      pasteAction()
    }
  }
}

/** Custom data keys of preview-only annotations, never saved into the document */
const previewAnnotationKeys = ['cropPreview', 'pageExclusionPreview']

const isPreviewAnnotation = (annotation: Core.Annotations.Annotation): boolean =>
  previewAnnotationKeys.some(key => annotation.getCustomData(key) !== '')

/** XFDF of the document's annotations without the viewer's preview overlays */
export const exportDocumentAnnotations = (
  annotationManager: Core.AnnotationManager,
): Promise<string> =>
  annotationManager.exportAnnotations({
    annotationList: annotationManager
      .getAnnotationsList()
      .filter(annotation => !isPreviewAnnotation(annotation)),
  })

/** Marks the given 1-based pages red and labels them as excluded from the Statikdokument */
export const applyPageExclusionPreview = (core: typeof Core, excludedPages: number[]): void => {
  const { annotationManager, documentViewer, Annotations } = core
  const document = documentViewer.getDocument()
  if (!document) return

  const previous = annotationManager
    .getAnnotationsList()
    .filter(annotation => annotation.getCustomData('pageExclusionPreview') !== '')
  if (previous.length) annotationManager.deleteAnnotations(previous, { force: true })

  const pageCount = document.getPageCount()
  const annotations = excludedPages
    .filter(pageNumber => pageNumber >= 1 && pageNumber <= pageCount)
    .flatMap(pageNumber => {
      // Page info is the displayed size, but annotations live in the unrotated page space,
      // so pages with an internal /Rotate of 90° or 270° (e.g. landscape plans) need it swapped
      const rotation = document.getPageRotation(pageNumber)
      const isQuarterTurned = rotation === 90 || rotation === 270
      const pageInfo = document.getPageInfo(pageNumber)
      const width = isQuarterTurned ? pageInfo.height : pageInfo.width
      const height = isQuarterTurned ? pageInfo.width : pageInfo.height
      // Properties are assigned after construction: the FreeText constructor
      // initializer resizes the annotation before it can measure its padding
      const setPreviewProps = (
        annotation: Core.Annotations.RectangleAnnotation | Core.Annotations.FreeTextAnnotation,
        rect: { x: number; y: number; width: number; height: number },
      ) => {
        annotation.PageNumber = pageNumber
        annotation.X = rect.x
        annotation.Y = rect.y
        annotation.Width = rect.width
        annotation.Height = rect.height
        annotation.StrokeThickness = 0
        annotation.ReadOnly = true
        annotation.Locked = true
        annotation.NoMove = true
        annotation.Listable = false
      }

      const overlay = new Annotations.RectangleAnnotation()
      setPreviewProps(overlay, { x: 0, y: 0, width, height })
      overlay.FillColor = new Annotations.Color(255, 0, 0, 0.3)

      const label = new Annotations.FreeTextAnnotation(
        Annotations.FreeTextAnnotation.Intent.FreeText,
      )
      // The label band runs across the displayed page, upright in the viewer
      setPreviewProps(
        label,
        isQuarterTurned
          ? { x: width / 2 - 30, y: 0, width: 60, height }
          : { x: 0, y: height / 2 - 30, width, height: 60 },
      )
      label.Rotation = rotation
      label.TextAlign = 'center'
      label.TextColor = new Annotations.Color(255, 0, 0, 1)
      label.FillColor = new Annotations.Color(0, 0, 0, 0)
      label.FontSize = '24pt'
      label.setContents('Seite Ausgeschlossen')

      return [overlay, label]
    })

  annotations.forEach(annotation =>
    annotation.setCustomData('pageExclusionPreview', `${annotation.PageNumber}`),
  )
  if (annotations.length) {
    annotationManager.addAnnotations(annotations)
    annotationManager.drawAnnotationsFromList(annotations)
  }
}
